import { loadAllFormSubmissions } from "@/lib/forms/loadAllSubmissions";
import { paginateSubmissions } from "@/lib/forms/submissionPagination";
import type { FormSource } from "@/lib/forms/formSource";
import { formatApiQueryError } from "@/lib/http/formatQueryError";
import type { PaginatedMeta } from "@/types/formList";
import { mergeSubmissionMarks } from "@/lib/forms/staffColumns";
import type { StaffMarkValue } from "@/types/staffColumn";
import type { FormSubmissionDto } from "@/types/submission";
import { useCallback, useMemo } from "react";
import { useAsyncResource } from "./useAsyncResource";

export const SUBMISSIONS_PER_PAGE_OPTIONS = [15, 25, 50] as const;

interface UseFormSubmissionsQueryOptions {
  formId: number | null;
  page: number;
  perPage: number;
  enabled?: boolean;
  source?: FormSource;
}

interface UseFormSubmissionsQueryResult {
  submissions: FormSubmissionDto[];
  allSubmissions: FormSubmissionDto[];
  meta: PaginatedMeta | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
  removeSubmission: (submissionId: number) => void;
  patchSubmissionMarksLocal: (
    submissionId: number,
    patch: Record<string, StaffMarkValue | null>,
  ) => void;
}

export function useFormSubmissionsQuery({
  formId,
  page,
  perPage,
  enabled = true,
  source = "native",
}: UseFormSubmissionsQueryOptions): UseFormSubmissionsQueryResult {
  const validFormId =
    enabled && formId != null && !Number.isNaN(formId) ? formId : null;

  const {
    data: allSubmissions = [],
    isLoading,
    error,
    reload,
    setData,
  } = useAsyncResource({
    enabled: validFormId != null,
    deps: [validFormId, source],
    fetch: () => loadAllFormSubmissions(validFormId!, { source }),
    formatError: (err) =>
      formatApiQueryError(err, "Не удалось загрузить ответы", {
        forbidden: "Нет доступа к ответам. Нужен scope manager или admin.",
        statusPrefix: "Не удалось загрузить ответы",
      }),
    initialLoading: false,
    clearOnError: true,
  });

  const removeSubmission = useCallback(
    (submissionId: number) => {
      setData((items) =>
        items ? items.filter((item) => item.id !== submissionId) : items,
      );
    },
    [setData],
  );

  const patchSubmissionMarksLocal = useCallback(
    (
      submissionId: number,
      patch: Record<string, StaffMarkValue | null>,
    ) => {
      setData((items) =>
        items
          ? items.map((item) =>
              item.id === submissionId
                ? { ...item, marks: mergeSubmissionMarks(item.marks, patch) }
                : item,
            )
          : items,
      );
    },
    [setData],
  );

  const { pageItems, sorted, meta } = useMemo(
    () => paginateSubmissions(allSubmissions, page, perPage),
    [allSubmissions, page, perPage],
  );

  return {
    submissions: pageItems,
    allSubmissions: sorted,
    meta,
    isLoading,
    error,
    reload,
    removeSubmission,
    patchSubmissionMarksLocal,
  };
}
